import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { ErrorHandleService } from '../errorHandleService/error-handle.service';
import { KeycloakService } from '../keycloakService/keycloak.service';
import { StorageService } from '../storageService/storage.service';
import { LOCAL_STORAGE_BRANCH_KEY, REFRESH_MESSAGE_KEY } from '@nagel-consts';
import { TabCommunicationService } from '../tabCommunicationService/tab-communication.service';

type ErrorItemValue = {
    error: string;
};

@Injectable({
    providedIn: 'root'
})
export class RequestService {

    constructor(
        private http: HttpClient,
        private errorHandleService: ErrorHandleService,
        private keycloakService: KeycloakService,
        private storageService: StorageService,
        private tabChannelService: TabCommunicationService
    ) { }

    private handleError = (error: HttpErrorResponse, closeActionLabel?: string) => {
        this.errorHandleService.showToasterError(error, closeActionLabel);
        this.errorHandleService.logError(error);
    };

    private buildCustomErrorMessage = (errorItems: ErrorItemValue[], responseCount: number) => {
        const errorMessages = errorItems.map((item) => item.error);
        const failedResponseCount = errorItems.length;

        const errorMessage = errorMessages.join(', ');
        const totalResponses = responseCount > 1 ? 'responses' : 'response';

        return `Failed to process ${failedResponseCount} out of ${responseCount} ${totalResponses}. ${errorMessage}`;

    };

    private getRequiredHeaders = () => {
        const token = this.keycloakService
            ? this.keycloakService.getToken() :
            '';

        const dbIdentifier = JSON.parse(this.storageService.getItem(LOCAL_STORAGE_BRANCH_KEY) || '{}')?.value || 'noIdentifier';

        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Database-Identifier': dbIdentifier
        };
    };

    private processResponse = <S>(response: S, sendRefreshEvent: boolean = true): S => {
        if (Array.isArray(response)) {
            const errorItems = response.filter((res) => res.error) || [];

            if (errorItems.length) {
                const customError = new HttpErrorResponse({
                    error: {
                        message: this.buildCustomErrorMessage(errorItems, response.length)
                    }
                });

                this.handleError(customError.error);
            }
        }

        if (sendRefreshEvent) {
            this.tabChannelService.sendMessage(REFRESH_MESSAGE_KEY);
        }

        return response;
    };

    public postRetrieveRequest<T = {}, R = {}, P = {}>(
        url: string,
        postBody?: T | object,
        params?: P | object,
        additionalHeaders?: { [key: string]: string },
    ): Observable<R> {
        const options = {
            headers: {
                ...this.getRequiredHeaders(),
                ...(additionalHeaders ?? {})
            },
            params: { ...(params ?? {}) }
        };

        const shouldSendRefreshEvent = false;

        return this.http.post<R>(url, postBody ?? {}, options)
            .pipe(
                map((response: R) => this.processResponse(response, shouldSendRefreshEvent)),
                tap({
                    error: e => {
                        this.handleError(e);
                    }
                })
            );
    }

    public postRequest<T = {}, R = {}, P = {}>(
        url: string,
        postBody?: T | object,
        params?: P | object,
        additionalHeaders?: { [key: string]: string },
        skipErrorHandling = false
    ): Observable<R> {
        const options = {
            headers: {
                ...this.getRequiredHeaders(),
                ...(additionalHeaders ?? {})
            },
            params: { ...(params ?? {}) }
        };

        const shouldSendRefreshEvent = true;

        return this.http.post<R>(url, postBody ?? {}, options)
            .pipe(
                map((response: R) => this.processResponse(response, shouldSendRefreshEvent)),
                tap({
                    error: e => {
                        if (!skipErrorHandling) {
                            this.handleError(e);
                        }
                    }
                })
            );
    }

    public getRequest<T, S>(url: string, params: T | object = {}, additionalHeaders: { [key: string]: string} = {}): Observable<S> {
        const options = {
            params: { ...params },
            headers: {
                ...this.getRequiredHeaders(),
                ...additionalHeaders
            }
        };

        return this.http.get<S>(url, options)
            .pipe(
                tap({
                    error: e => {
                        this.handleError(e);
                    }
                })
            );
    }

    public getRequestWithFullResponse<T, S>(url: string, params: T | object = {}, additionalHeaders: { [key: string]: string} = {}): Observable<HttpResponse<S>> {
        const options = {
            params: { ...params },
            headers: {
                ...this.getRequiredHeaders(),
                ...additionalHeaders
            },
            observe: 'response' as const
        };

        return this.http.get<S>(url, options)
            .pipe(
                tap({
                    error: e => {
                        this.handleError(e);
                    }
                })
            );
    }

    public deleteRequest<T, S>(
        url: string,
        params: T | object = {},
        postBody: T | object = {},
        additionalHeaders: { [key: string]: string} = {},
        skipErrorHandling = false
    ): Observable<S> {
        const options = {
            params: { ...params },
            headers: {
                ...this.getRequiredHeaders(),
                ...additionalHeaders
            },
            body: postBody
        };

        return this.http.delete<S>(url, options)
            .pipe(
                map((response: S) => this.processResponse(response)),
                tap({
                    error: e => {
                        if (!skipErrorHandling) {
                            this.handleError(e);
                        }
                    }
                })
            );
    }

    public patchRequest<T = {}, R = {}, P = {}>(
        url: string,
        postBody?: T | object,
        params?: P | object,
        additionalHeaders: { [key: string]: string} = {}
    ): Observable<R> {
        const options = {
            headers: {
                ...this.getRequiredHeaders(),
                ...(additionalHeaders ?? {})
            },
            params: { ...(params ?? {}) }
        };

        return this.http.patch<R>(url, postBody ?? {}, options)
            .pipe(
                map((response: R) => this.processResponse(response)),
                tap({
                    error: e => {
                        this.handleError(e);
                    }
                })
            );
    }

    public putRequest<T, S>(
        url: string,
        postBody: T | object = {},
        additionalHeaders: { [key: string]: string} = {},
        skipErrorHandling = false
    ): Observable<S> {
        const options = {
            headers: {
                ...this.getRequiredHeaders(),
                ...additionalHeaders
            }
        };

        return this.http.put<S>(url, postBody, options)
            .pipe(
                map((response: S) => this.processResponse(response)),
                tap({
                    error: e => {
                        if (!skipErrorHandling) {
                            this.handleError(e);
                        }
                    }
                })
            );
    }
}